import type { GetRewardFiltersResponseType, RewardFilterType } from "@jti/rest-api-types";

import type { SimplifiedPrizesSubcategoryOption } from "../SimplifiedPrizesFilters";

const BE_TO_FILTER_CATEGORY_KEY: Record<string, RewardFilterType> = {
  virtual: "virtual",
  physical: "physical",
  lottery: "lottery",
  sensory_experience: "sensorial",
};

export type SubcategoryTranslation = {
  key: string;
  value: string;
};

const normalizeSubcategoryKey = (key: string) => key.normalize().trim().toLocaleLowerCase();

const isValidSubcategoryTranslation = (
  translation: SubcategoryTranslation | null | undefined
): translation is SubcategoryTranslation =>
  typeof translation?.key === "string" &&
  translation.key.trim().length > 0 &&
  typeof translation.value === "string" &&
  translation.value.trim().length > 0;

export const mapRewardsFiltersToSubcategoryOptions = (
  rewardsFilters?: GetRewardFiltersResponseType,
  subcategoryTranslations: readonly SubcategoryTranslation[] = []
): SimplifiedPrizesSubcategoryOption[] => {
  if (!rewardsFilters) return [];

  const translationsBySubcategory = new Map(
    subcategoryTranslations
      .filter(isValidSubcategoryTranslation)
      .map(({ key, value }) => [normalizeSubcategoryKey(key), value])
  );
  const dynamicSubcategoryOptions = Object.entries(rewardsFilters.categories).flatMap(([backendKey, category]) => {
    const parentCategoryId = BE_TO_FILTER_CATEGORY_KEY[backendKey];

    return category.subcategories.map((label) => ({
      id: `${parentCategoryId}:${label}`,
      label: translationsBySubcategory.get(normalizeSubcategoryKey(label)) ?? label,
      parentCategoryId,
    }));
  });

  return dynamicSubcategoryOptions;
};

export const getDisabledCategoryKeysFromTotals = (
  rewardsFilters?: GetRewardFiltersResponseType
): Set<RewardFilterType> => {
  const disabledCategoryKeys = new Set<RewardFilterType>();
  if (!rewardsFilters) return disabledCategoryKeys;

  Object.entries(rewardsFilters.categories).forEach(([backendKey, category]) => {
    const filterKey = BE_TO_FILTER_CATEGORY_KEY[backendKey];
    if (filterKey && category.total === 0) {
      disabledCategoryKeys.add(filterKey);
    }
  });

  return disabledCategoryKeys;
};

export const getCategoryTotals = (rewardsFilters?: GetRewardFiltersResponseType): Map<RewardFilterType, number> => {
  const categoryTotals = new Map<RewardFilterType, number>();
  if (!rewardsFilters) return categoryTotals;

  Object.entries(rewardsFilters.categories).forEach(([backendKey, category]) => {
    const filterKey = BE_TO_FILTER_CATEGORY_KEY[backendKey] || backendKey;
    if (filterKey) {
      categoryTotals.set(filterKey, category.total);
    }
  });

  return categoryTotals;
};
